import React, { useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { Check } from 'lucide-react';
import { approachData } from '../data/mock';

/**
 * ApproachSection Component
 * 
 * Section "Notre Méthode" avec 4 phases:
 * - Animation d'entrée en séquence au scroll (whileInView)
 * - Effet hover qui met en avant la carte survolée
 * - Timeline interactive
 */
const ApproachSection = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: false, margin: '-10%' });
  const [activePhase, setActivePhase] = useState(0);
  const [hoveredCard, setHoveredCard] = useState(null);
  
  // Animation variants for staggered entry
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2,
      },
    },
  };
  
  const cardVariants = {
    hidden: { opacity: 0, y: 40, scale: 0.95 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        type: 'spring',
        damping: 20,
        stiffness: 100,
      },
    },
  };
  
  return (
    <section
      id="approche"
      ref={sectionRef}
      className="section-container bg-[#0A0A0A]"
    >
      {/* Section Header */}
      <motion.div
        className="text-center mb-20"
        initial={{ opacity: 0, y: 30 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.6 }}
      >
        <p className="text-[#2A3B8F] uppercase tracking-widest text-sm mb-4">
          Notre méthode
        </p>
        <h2 className="heading-lg mb-6">
          Une approche en quatre temps
        </h2>
        <p className="body-md text-[#888888] max-w-2xl mx-auto">
          Chaque projet suit un rythme précis, dicté par les opportunités stratégiques 
          plutôt que par un calendrier arbitraire.
        </p>
      </motion.div>
      
      {/* Timeline Navigation */}
      <motion.div
        className="hidden lg:flex justify-center mb-16"
        initial={{ opacity: 0 }}
        animate={isInView ? { opacity: 1 } : {}}
        transition={{ delay: 0.3 }}
      >
        <div className="flex items-center gap-0">
          {approachData.map((phase, index) => (
            <React.Fragment key={phase.id}>
              <motion.button
                onClick={() => setActivePhase(index)}
                className="relative flex flex-col items-center interactive"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <motion.div
                  className="w-14 h-14 rounded-full flex items-center justify-center border-2 transition-all duration-300"
                  animate={{
                    backgroundColor: activePhase === index ? '#2A3B8F' : 'transparent',
                    borderColor: activePhase === index ? '#2A3B8F' : '#555555',
                    scale: activePhase === index ? 1.1 : 1,
                  }}
                >
                  <span className={`font-semibold ${
                    activePhase === index ? 'text-white' : 'text-[#888888]'
                  }`}>
                    {phase.phase}
                  </span>
                </motion.div>
                <motion.span
                  className="mt-3 text-xs uppercase tracking-wider"
                  animate={{
                    color: activePhase === index ? '#FFFFFF' : '#555555',
                  }}
                >
                  {phase.title.split(' ')[0]}
                </motion.span>
              </motion.button>
              
              {index < approachData.length - 1 && (
                <motion.div
                  className="w-24 h-px mx-2"
                  animate={{
                    backgroundColor: activePhase > index ? '#2A3B8F' : '#333333',
                  }}
                  transition={{ duration: 0.3 }}
                />
              )}
            </React.Fragment>
          ))}
        </div>
      </motion.div>
      
      {/* Phase Cards Grid */}
      <motion.div
        className="grid md:grid-cols-2 lg:grid-cols-4 gap-6"
        variants={containerVariants}
        initial="hidden"
        animate={isInView ? 'visible' : 'hidden'}
      >
        {approachData.map((phase, index) => (
          <motion.div
            key={phase.id}
            variants={cardVariants}
            className={`relative p-6 bg-[#1A1A1A] border transition-all duration-300 interactive ${
              activePhase === index
                ? 'border-[#2A3B8F] shadow-lg shadow-[#2A3B8F]/10'
                : 'border-white/5'
            } ${
              hoveredCard !== null && hoveredCard !== index
                ? 'opacity-50 scale-[0.98]'
                : 'opacity-100 scale-100'
            }`}
            onClick={() => setActivePhase(index)}
            onMouseEnter={() => setHoveredCard(index)}
            onMouseLeave={() => setHoveredCard(null)}
            whileHover={{
              y: -8,
              transition: { type: 'spring', stiffness: 300 },
            }}
          >
            {/* Phase number */}
            <motion.span
              className="text-5xl font-bold mb-4 block"
              animate={{
                color: activePhase === index ? '#2A3B8F' : '#333333',
              }}
            >
              {phase.phase}
            </motion.span>
            
            {/* Duration */}
            <span className="text-[#555555] text-xs uppercase tracking-wider">
              {phase.duration}
            </span>
            
            {/* Title */}
            <h3 className="text-lg font-semibold text-white mt-2 mb-3">
              {phase.title}
            </h3>
            
            {/* Tagline */}
            <p className="text-[#2A3B8F] text-sm italic mb-4">
              "{phase.tagline}"
            </p>
            
            {/* Points (visible on active/hover) */}
            <motion.ul
              className="space-y-2"
              initial={{ opacity: 0, height: 0 }}
              animate={{
                opacity: activePhase === index || hoveredCard === index ? 1 : 0.5,
                height: 'auto',
              }}
              transition={{ duration: 0.3 }}
            >
              {phase.points.map((point, i) => (
                <li key={i} className="flex items-start gap-2 text-sm">
                  <Check className="w-4 h-4 text-[#2A3B8F] mt-0.5 flex-shrink-0" />
                  <span className="text-[#888888]">{point}</span>
                </li>
              ))}
            </motion.ul>
            
            {/* Active indicator */}
            <motion.div
              className="absolute bottom-0 left-0 h-1 bg-[#2A3B8F]"
              initial={{ width: 0 }}
              animate={{ width: activePhase === index ? '100%' : 0 }}
              transition={{ duration: 0.3 }}
            />
          </motion.div>
        ))}
      </motion.div>
    </section>
  );
};

export default ApproachSection;
